import { el } from "../util/dom";
import { sendFeedback } from "../api/admin";
import { isLoggedIn } from "../api/auth";
import { ApiError } from "../api/client";

export const FEEDBACK_MAX = 2000;

/** Client-side checks before POST /v1/support/feedback (the server enforces
 * the same 1..2000 range). Returns the error copy to show, or null. */
export function validateFeedback(message: string, contactEmail: string, signedIn: boolean): string | null {
  const text = message.trim();
  if (!text) return "Enter a message.";
  if (text.length > FEEDBACK_MAX) return `Keep it under ${FEEDBACK_MAX} characters.`;
  const email = contactEmail.trim();
  if (!signedIn && email && (!email.includes("@") || email.startsWith("@") || email.endsWith("@"))) {
    return "Enter a valid email address, or leave it blank.";
  }
  return null;
}

export interface FeedbackView {
  root: HTMLElement;
  /** Signed in: the API stamps the account, so the contact field is hidden. */
  setSignedIn(signedIn: boolean): void;
}

/** SW-29: the FEEDBACK screen (/feedback), open to everyone. */
export function buildFeedbackView(): FeedbackView {
  const textarea = el("textarea", {
    class: "auth-input cancel-reason-input",
    placeholder: "What should we know?",
    maxlength: String(FEEDBACK_MAX),
    rows: "5",
  }) as HTMLTextAreaElement;
  const counter = el("p", { class: "auth-forgot-status" }, [`0 / ${FEEDBACK_MAX}`]);
  const emailInput = el("input", {
    type: "email",
    placeholder: "email (optional, if you'd like a reply)",
    class: "auth-input",
    autocomplete: "email",
  }) as HTMLInputElement;
  const emailRow = el("div", { class: "auth-row" }, [emailInput]);
  const statusEl = el("p", { class: "auth-error" });
  const sendBtn = el("button", { class: "auth-btn" }, ["SEND"]) as HTMLButtonElement;
  let signedIn = false;

  textarea.addEventListener("input", () => {
    counter.textContent = `${textarea.value.length} / ${FEEDBACK_MAX}`;
  });

  async function submit(): Promise<void> {
    const problem = validateFeedback(textarea.value, emailInput.value, signedIn);
    statusEl.className = "auth-error";
    statusEl.textContent = problem ?? "";
    if (problem) return;
    sendBtn.disabled = true;
    try {
      const email = emailInput.value.trim();
      await sendFeedback(textarea.value.trim(), signedIn || !email ? null : email);
      textarea.value = "";
      counter.textContent = `0 / ${FEEDBACK_MAX}`;
      statusEl.className = "subscribe-status";
      statusEl.textContent = "Thanks — sent.";
    } catch (err) {
      statusEl.textContent =
        err instanceof ApiError ? err.detail : err instanceof Error ? err.message : String(err);
    } finally {
      sendBtn.disabled = false;
    }
  }
  sendBtn.addEventListener("click", () => void submit());

  const root = el("div", { class: "auth-panel" }, [
    el("div", { class: "section-title" }, ["Tell us what you think"]),
    el("div", { class: "auth-row" }, [textarea]),
    counter,
    emailRow,
    el("div", { class: "auth-row" }, [sendBtn]),
    statusEl,
  ]);

  function setSignedIn(value: boolean): void {
    signedIn = value;
    emailRow.style.display = value ? "none" : "";
  }
  setSignedIn(isLoggedIn());

  return { root, setSignedIn };
}
